/**
 * The cabinet behind the page: a warm light from above and a small cluster of
 * bottles standing in the right-hand margin, both at hairline contrast.
 *
 * `design-direction.md` refuses bottle photography as decoration, and this is
 * not that — there is no photograph, no texture and no image file, only flat
 * vector shapes at a few per cent opacity. What it buys is the one thing a
 * flat ground could not say: that the list in front of you is the inside of a
 * cabinet. The rule it must keep is that nothing here competes with the
 * drinking-window band, which is why the whole layer lives at an alpha low
 * enough that a screenshot of a row looks unchanged.
 *
 * Fixed rather than in flow, so it does not scroll with a long wine list and
 * costs one composited layer instead of a repaint per frame; `print:hidden`,
 * because the label sheet and the wine page are printed on paper that has no
 * cellar behind it.
 *
 * On the right, because the left gutter is where the wine number lives and
 * that gutter is structural. Nothing decorative goes in it.
 */

/**
 * The cluster: `[x, scale, base]` in its own coordinates, against a bottle
 * drawn 56 wide and 220 tall.
 *
 * Five bottles standing together rather than a row across the page, because a
 * row is a fence and a fence is a graphic device; bottles that somebody put
 * down near each other are a cellar. Three stand further back with their bases
 * higher and their glass fainter, two stand in front of the gaps between them,
 * which is the whole of the depth here — no perspective, no shadow, just two
 * base lines and two opacities.
 */
const BACK_ROW: readonly (readonly [number, number, number])[] = [
  [0, 1.15, 306],
  [70, 1.3, 306],
  [148, 1.08, 306],
];

const FRONT_ROW: readonly (readonly [number, number, number])[] = [
  [28, 1.42, 340],
  [112, 1.32, 340],
];

function Bottle([x, scale, base]: readonly [number, number, number]) {
  return (
    <use
      key={`${x}-${scale}`}
      href="#corklog-bottle"
      transform={`translate(${x} ${base - 220 * scale}) scale(${scale})`}
    />
  );
}

export function CellarBackdrop() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden print:hidden"
    >
      {/* The cellar light. A single warm ellipse high on the page, so the
          ground is brighter where the eye starts and settles into shadow at
          the bottom where the bottles stand. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 70% at 50% -10%, var(--ck-backdrop-glow), transparent 70%)",
        }}
      />

      {/* Sized by its height rather than its width: what must stay true at
          every viewport is that the bottles are about as tall as a third of
          the screen and standing on its bottom edge, and the width follows
          from the cluster's own proportions. The blur is what makes this a
          memory of a cabinet rather than an illustration of one — it also
          hides the banding a flat vector edge shows at five per cent. */}
      <svg
        viewBox="0 0 210 340"
        preserveAspectRatio="xMidYMax meet"
        className="absolute bottom-0 blur-[2px]"
        style={{
          color: "var(--ck-backdrop-glass)",
          height: "clamp(260px, 46vh, 480px)",
          aspectRatio: "210 / 340",
          /* The cluster ends where the rules in the list end. Half the measure
             out from the centre, less the gutter, is the page's right-hand
             edge; `max` holds the cluster at the gutter once the viewport is
             narrower than the measure and there is no margin left to stand
             in. Lining up with the grid rather than floating in the margin is
             what stops it reading as a sticker on the glass. */
          right:
            "max(var(--ck-page-gutter), calc(50% - var(--ck-measure) / 2 + var(--ck-page-gutter)))",
        }}
      >
        <defs>
          {/* Each bottle fades over its own height rather than the layer's:
              the neck is almost nothing, the shoulder is where the light
              catches, and the base sinks back into the shadow it stands in. */}
          <linearGradient id="corklog-glass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.12" />
            <stop offset="32%" stopColor="currentColor" stopOpacity="1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
          </linearGradient>

          {/* One bottle in true proportions — 56 wide to 220 tall, a real
              bottle's quarter — drawn once and stood up five times. The mark
              in the header exaggerates its width to survive sixteen pixels;
              nothing has to be shouted at this size. */}
          <path
            id="corklog-bottle"
            fill="url(#corklog-glass)"
            d="M20 0 H36 V50 C36 62 40 66 45 74 C52 84 56 94 56 108 V214 C56 217.3 53.3 220 50 220 H6 C2.7 220 0 217.3 0 214 V108 C0 94 4 84 11 74 C16 66 20 62 20 50 Z"
          />
        </defs>

        <g style={{ opacity: "calc(var(--ck-backdrop-alpha) * 0.62)" }}>
          {BACK_ROW.map(Bottle)}
        </g>
        <g style={{ opacity: "var(--ck-backdrop-alpha)" }}>{FRONT_ROW.map(Bottle)}</g>
      </svg>
    </div>
  );
}
